import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FormInput, FormTextarea, RequirementsList } from '../common';
import AvailableToolsSidebar from './AvailableToolsSidebar';
import SelectedToolsCards from './SelectedToolsCards';
import { getTools } from '../../api/client';

/** Tool identifier per schema (identificationId). */
function getToolId(tool) {
  if (tool == null) return undefined;
  return tool?.identificationId ?? tool?.id ?? tool?._id?.$oid ?? tool?._id;
}

function getToolName(tool) {
  if (tool == null) return '';
  return tool?.name ?? tool?.title ?? '';
}

const NON_EDITABLE_KEYS = {
  identificationId: 1, id: 1, _id: 1, name: 1, title: 1,
  createdAt: 1, updatedAt: 1, created_at: 1, updated_at: 1,
};
/** Read height/width from requiredSize object; coerce to number. */
function sizeFromRequiredSize(requiredSize) {
  if (requiredSize == null || typeof requiredSize !== 'object') return {};
  return {
    height: requiredSize.height != null ? Number(requiredSize.height) : undefined,
    width: requiredSize.width != null ? Number(requiredSize.width) : undefined,
  };
}

/** When loading from API, item may have height/width only inside requiredSize. Copy them to top level so the form can show them. */
function normalizeEntryFromApi(item, rest) {
  const requiredSize = item?.requiredSize ?? item?.required_size ?? rest?.requiredSize ?? rest?.required_size;
  const { height: fromSizeH, width: fromSizeW } = sizeFromRequiredSize(requiredSize);
  const out = { ...rest };
  if (out.height == null && fromSizeH != null) out.height = fromSizeH;
  if (out.width == null && fromSizeW != null) out.width = fromSizeW;
  delete out.requiredSize;
  delete out.required_size;
  return out;
}

function getDefaultConfigFromTool(tool) {
  const requiredSize = tool?.requiredSize ?? tool?.required_size;
  const hasRequiredSize = requiredSize != null;
  const { height: defaultHeight, width: defaultWidth } = sizeFromRequiredSize(requiredSize);

  if (tool?.defaultConfig && typeof tool.defaultConfig === 'object' && !Array.isArray(tool.defaultConfig)) {
    const c = { ...tool.defaultConfig };
    Object.keys(NON_EDITABLE_KEYS).forEach((k) => delete c[k]);
    delete c.requiredSize;
    delete c.required_size;
    if (!hasRequiredSize) {
      delete c.height;
      delete c.width;
    } else {
      c.height = c.height != null ? Number(c.height) : defaultHeight;
      c.width = c.width != null ? Number(c.width) : defaultWidth;
    }
    return c;
  }
  const config = {};
  for (const [k, v] of Object.entries(tool || {})) {
    if (NON_EDITABLE_KEYS[k] || v === undefined || k === 'requiredSize' || k === 'required_size') continue;
    if (k === 'height' || k === 'width') {
      if (!hasRequiredSize) continue;
    }
    if (k === 'defaultConfig' && typeof v === 'object') {
      Object.assign(config, v);
      Object.keys(NON_EDITABLE_KEYS).forEach((key) => delete config[key]);
      delete config.requiredSize;
      delete config.required_size;
      if (!hasRequiredSize) {
        delete config.height;
        delete config.width;
      } else {
        config.height = config.height != null ? Number(config.height) : defaultHeight;
        config.width = config.width != null ? Number(config.width) : defaultWidth;
      }
    } else {
      config[k] = v;
    }
  }
  if (hasRequiredSize) {
    if (config.height == null) config.height = defaultHeight;
    if (config.width == null) config.width = defaultWidth;
  }
  if (!('label' in config) && !('name' in config)) {
    config.label = getToolName(tool);
  }
  return config;
}

export default function TemplateForm({ initialData, onSubmit, loading: submitLoading, error: submitError }) {
  const [title, setTitle] = useState(initialData?.title ?? '');
  const [description, setDescription] = useState(initialData?.description ?? '');
  const [instructions, setInstructions] = useState(initialData?.instructions ?? '');
  const [requirements, setRequirements] = useState(
    Array.isArray(initialData?.requirements) ? initialData.requirements : []
  );
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [availableTools, setAvailableTools] = useState([]);
  const [toolsLoading, setToolsLoading] = useState(true);
  const [toolsError, setToolsError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setToolsLoading(true);
    setToolsError(null);
    getTools(0, 200)
      .then((res) => {
        if (!cancelled) setAvailableTools(res?.listOfItems ?? []);
      })
      .catch((err) => {
        if (!cancelled) setToolsError(err?.body?.message || err?.message || 'Failed to load tools');
      })
      .finally(() => {
        if (!cancelled) setToolsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const initialToolsSynced = useRef(false);
  useEffect(() => {
    if (!Array.isArray(initialData?.tools) || initialData.tools.length === 0) return;
    if (initialToolsSynced.current) return;
    const raw = initialData.tools;
    const resolved = raw.map((item) => {
      const isObj = typeof item === 'object' && item !== null;
      const identificationId = isObj
        ? (item.identificationId ?? item.toolId ?? item.tool_id ?? getToolId(item.tool))
        : item;
      const tool = isObj && item.tool
        ? item.tool
        : availableTools.find((a) => getToolId(a) === identificationId) || { identificationId, id: identificationId, _id: identificationId, name: `Tool ${identificationId}` };
      let rest = isObj ? { ...item } : {};
      delete rest.identificationId;
      delete rest.toolId;
      delete rest.tool_id;
      delete rest.tool;
      rest = normalizeEntryFromApi(item, rest);
      return { identificationId, tool, ...rest };
    });
    setSelectedEntries(resolved);
    if (availableTools.length > 0 || raw.some((t) => typeof t === 'object' && t !== null && (t.identificationId != null || t.toolId != null || t.tool_id != null || t.tool != null))) {
      initialToolsSynced.current = true;
    }
  }, [initialData?.tools, availableTools]);

  const handleAddTool = useCallback((tool) => {
    const identificationId = getToolId(tool);
    const defaultConfig = getDefaultConfigFromTool(tool);
    setSelectedEntries((prev) => [...prev, { identificationId, tool, ...defaultConfig }]);
  }, []);

  const handleRemoveEntry = useCallback((index) => {
    setSelectedEntries((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleUpdateEntry = useCallback((index, updated) => {
    setSelectedEntries((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updated };
      return next;
    });
  }, []);

  const handleMoveUp = useCallback((index) => {
    if (index <= 0) return;
    setSelectedEntries((prev) => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  }, []);

  const handleMoveDown = useCallback((index) => {
    setSelectedEntries((prev) => {
      if (index >= prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const stripKeys = { tool: 1, toolId: 1, ...NON_EDITABLE_KEYS };
    const tools = selectedEntries.map((entry) => {
      const identificationId = entry.identificationId ?? entry.toolId;
      const out = { identificationId };
      for (const [k, v] of Object.entries(entry)) {
        if (stripKeys[k] || k === 'tool') continue;
        if (k === 'identificationId') continue; // already set
        out[k] = v;
      }
      return out;
    });
    onSubmit({
      title: title.trim(),
      description: description.trim() || undefined,
      instructions: instructions.trim() || undefined,
      requirements: requirements.filter((s) => String(s).trim()).length > 0
        ? requirements.map((s) => s.trim()).filter(Boolean)
        : undefined,
      tools: tools.length > 0 ? tools : undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="template-form">
      <div className="template-form__layout">
        <div className="template-form__left">
          <div className="card">
            <div className="card-body">
              {submitError && (
                <div className="alert alert-danger py-2 small mb-3" role="alert">
                  {submitError}
                </div>
              )}
              <FormInput label="Title" value={title} onChange={setTitle} placeholder="Template title" required />
              <FormTextarea label="Description" value={description} onChange={setDescription} placeholder="Short description" rows={2} />
              <FormTextarea label="Instructions" value={instructions} onChange={setInstructions} placeholder="Instructions for the user" rows={4} />
              <RequirementsList value={requirements} onChange={setRequirements} />

              <SelectedToolsCards
                selectedEntries={selectedEntries}
                onRemoveEntry={handleRemoveEntry}
                onMoveUp={handleMoveUp}
                onMoveDown={handleMoveDown}
                onUpdateEntry={handleUpdateEntry}
              />

              <div className="template-form__actions mt-4">
                <button type="submit" className="btn btn-primary" disabled={submitLoading}>
                  {submitLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Saving...
                    </>
                  ) : (
                    'Save template'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="template-form__right">
          <AvailableToolsSidebar
            availableTools={availableTools}
            onAddTool={handleAddTool}
            loading={toolsLoading}
            error={toolsError}
          />
        </div>
      </div>
    </form>
  );
}
